import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, IndianRupee, MessageCircle, ShieldCheck, Smartphone, Sparkles, Star, Zap,
} from "lucide-react";
import api, { extractErr } from "@/lib/api";
import { PLATFORM_LOGO_URL, platformLogoUrl } from "@/lib/brand";
import "./GadsLanding.css";

const LOGO_URL = PLATFORM_LOGO_URL;
const SIGNUP_HREF = "/signup";
const POPUP_MAX = 2;
const POPUP_GAP_MS = 30000;
const POPUP_COUNT_KEY = "gads_popup_count";
const POPUP_LAST_KEY = "gads_popup_last";
const LEAD_OK_KEY = "gads_lead_ok";

const EMPTY = {
  name: "", phone: "", email: "", business_category: "", when_to_start: "",
};

const FUNNEL = [
  { n: "01", title: "Book a 15-min demo", body: "Tell us your store type. No pitch deck." },
  { n: "02", title: "See GST checkout live", body: "UPI, COD and WhatsApp on a real store." },
  { n: "03", title: "Go live for ₹1,999", body: "Pay in rupees. Sell the same day." },
];

function qs() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      gclid: p.get("gclid") || p.get("gclid") || "",
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
      utm_content: p.get("utm_content") || "",
      utm_term: p.get("utm_term") || "",
    };
  } catch {
    return {};
  }
}

function leadDone() {
  try { return sessionStorage.getItem(LEAD_OK_KEY) === "1"; } catch { return false; }
}

function popupMeta() {
  try {
    return {
      count: Number(sessionStorage.getItem(POPUP_COUNT_KEY) || 0),
      last: Number(sessionStorage.getItem(POPUP_LAST_KEY) || 0),
    };
  } catch {
    return { count: 0, last: 0 };
  }
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

function hasScript(needle) {
  return Boolean(document.querySelector(`script[src*="${needle}"]`));
}

function injectClarity(projectId) {
  const id = String(projectId || "").trim();
  if (!/^[A-Za-z0-9._-]{2,32}$/.test(id)) return;
  if (window.__gadsClarityInjected || hasScript("clarity.ms/tag")) return;
  window.__gadsClarityInjected = true;
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", id);
}

function fireConversion(conv) {
  const sendTo = conv?.send_to;
  const value = Number(conv?.value || 1);
  const currency = conv?.currency || "INR";
  try {
    ensureGtag();
    window.dataLayer.push({ event: "gads_lead_submit", value, currency, send_to: sendTo });
    window.gtag("event", "generate_lead", { value, currency });
    if (sendTo) {
      window.gtag("event", "conversion", { send_to: sendTo, value, currency });
    }
    if (typeof window.clarity === "function") {
      window.clarity("event", "gads_lead_submit");
    }
  } catch { /* ignore */ }
}

function LeadForm({ c, placement, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const cats = c.categories || [];
  const times = c.timelines || [];
  const f = c.content?.form || {};

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/gads/lead", {
        ...form,
        placement,
        page_url: window.location.href,
        ...qs(),
      });
      setOk(f.success || "Thanks! We will call or WhatsApp you shortly.");
      fireConversion(data.conversion);
      try { sessionStorage.setItem(LEAD_OK_KEY, "1"); } catch { /* ignore */ }
      onDone?.();
    } catch (ex) {
      setErr(extractErr(ex) || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (ok) return <div className="gads-ok" data-testid="gads-form-success">{ok}</div>;
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={submit} data-testid={`gads-form-${placement}`}>
      <div className="gads-field">
        <label htmlFor={`gads-name-${placement}`}>Name</label>
        <input id={`gads-name-${placement}`} required autoComplete="name" value={form.name} onChange={set("name")} placeholder="Your full name" />
      </div>
      <div className="gads-field">
        <label htmlFor={`gads-phone-${placement}`}>Phone</label>
        <input id={`gads-phone-${placement}`} required autoComplete="tel" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" inputMode="tel" maxLength={13} />
      </div>
      <div className="gads-field">
        <label htmlFor={`gads-email-${placement}`}>Email</label>
        <input id={`gads-email-${placement}`} type="email" required autoComplete="email" value={form.email} onChange={set("email")} placeholder="you@business.in" />
      </div>
      <div className="gads-field">
        <label htmlFor={`gads-cat-${placement}`}>Business category</label>
        <select id={`gads-cat-${placement}`} required value={form.business_category} onChange={set("business_category")}>
          <option value="">Select category</option>
          {cats.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div className="gads-field">
        <label htmlFor={`gads-when-${placement}`}>When are you thinking to start?</label>
        <select id={`gads-when-${placement}`} required value={form.when_to_start} onChange={set("when_to_start")}>
          <option value="">Select timeline</option>
          {times.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      {err ? <div className="gads-err">{err}</div> : null}
      <button type="submit" className="gads-btn gads-btn-primary" disabled={busy} data-testid={`gads-submit-${placement}`}>
        {busy ? "Sending…" : (f.submit_label || "Book my free demo")}
        {!busy ? <ArrowRight size={16} /> : null}
      </button>
    </form>
  );
}

function Tracking({ t }) {
  const gtm = (t.gtm_id || "").trim();
  const ga4 = (t.ga4_id || "").trim();
  const ads = (t.google_ads_id || "").trim();
  const clarity = (t.clarity_id || "").trim();
  const hotjar = (t.hotjar_id || "").trim();
  const hjSv = t.hotjar_sv || "6";
  const pixel = (t.fb_pixel_id || "").trim();

  useEffect(() => {
    ensureGtag();
    if (gtm && !hasScript("googletagmanager.com/gtm.js") && !window.__gadsGtmInjected) {
      window.__gadsGtmInjected = true;
      (function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s);
        const dl = l !== "dataLayer" ? `&l=${l}` : "";
        j.async = true;
        j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
        f.parentNode.insertBefore(j, f);
      })(window, document, "script", "dataLayer", gtm);
    }
    if ((ga4 || ads) && !hasScript("googletagmanager.com/gtag/js")) {
      const el = document.createElement("script");
      el.async = true;
      el.src = `https://www.googletagmanager.com/gtag/js?id=${ga4 || ads}`;
      document.head.appendChild(el);
    }
    if (!window.__gadsGtagConfigured && (ga4 || ads)) {
      window.__gadsGtagConfigured = true;
      window.gtag("js", new Date());
      if (ga4) window.gtag("config", ga4);
      if (ads) window.gtag("config", ads);
    }
    injectClarity(clarity);
    if (hotjar && !window.__gadsHotjarInjected && !hasScript("static.hotjar.com/c/hotjar-")) {
      window.__gadsHotjarInjected = true;
      (function (h, o, t, j, a, r) {
        h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); };
        h._hjSettings = { hjid: hotjar, hjsv: hjSv };
        a = o.getElementsByTagName("head")[0];
        r = o.createElement("script");
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
      })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
    }
    if (pixel && !window.__gadsPixelInjected && !hasScript("connect.facebook.net/en_US/fbevents.js")) {
      window.__gadsPixelInjected = true;
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      window.fbq("init", pixel);
      window.fbq("track", "PageView");
    }
  }, [gtm, ga4, ads, clarity, hotjar, hjSv, pixel]);

  return null;
}

export default function GadsLanding() {
  const [page, setPage] = useState(null);
  const [popup, setPopup] = useState(false);
  const openRef = useRef(false);

  useEffect(() => {
    ensureGtag();
  }, []);

  useEffect(() => {
    api.get("/gads/page").then(({ data }) => setPage(data)).catch(() => setPage({ content: {}, tracking: {}, categories: [], timelines: [] }));
  }, []);

  const tryOpenPopup = useCallback(() => {
    if (openRef.current || popup || leadDone()) return false;
    const { count, last } = popupMeta();
    if (count >= POPUP_MAX) return false;
    if (count > 0 && Date.now() - last < POPUP_GAP_MS) return false;
    openRef.current = true;
    try {
      sessionStorage.setItem(POPUP_COUNT_KEY, String(count + 1));
      sessionStorage.setItem(POPUP_LAST_KEY, String(Date.now()));
    } catch { /* ignore */ }
    setPopup(true);
    return true;
  }, [popup]);

  const closePopup = useCallback(() => {
    setPopup(false);
    openRef.current = false;
    const { count } = popupMeta();
    if (count < POPUP_MAX && !leadDone()) {
      window.setTimeout(() => { tryOpenPopup(); }, POPUP_GAP_MS);
    }
  }, [tryOpenPopup]);

  useEffect(() => {
    if (leadDone()) return undefined;
    const first = window.setTimeout(() => { tryOpenPopup(); }, 12000);
    let scrolled = false;
    const onScroll = () => {
      if (scrolled) return;
      const h = document.documentElement;
      const pct = (h.scrollTop + window.innerHeight) / Math.max(1, h.scrollHeight);
      if (pct > 0.45) {
        scrolled = true;
        tryOpenPopup();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(first);
      window.removeEventListener("scroll", onScroll);
    };
  }, [tryOpenPopup]);

  const beacon = useCallback((events) => {
    if (!page?.tracking?.heatmap_enabled) return;
    api.post("/gads/events", { events }).catch(() => {});
  }, [page]);

  useEffect(() => {
    if (!page?.tracking?.heatmap_enabled) return undefined;
    const clicks = (e) => {
      const tag = (e.target?.tagName || "div") + (e.target?.innerText ? `:${String(e.target.innerText).slice(0, 24)}` : "");
      beacon([{ type: "click", x: e.clientX, y: e.clientY, tag }]);
    };
    let last = 0;
    const scrolls = () => {
      const now = Date.now();
      if (now - last < 2500) return;
      last = now;
      const h = document.documentElement;
      const depth = Math.round(((h.scrollTop + window.innerHeight) / Math.max(1, h.scrollHeight)) * 100);
      beacon([{ type: "scroll", depth }]);
    };
    window.addEventListener("click", clicks);
    window.addEventListener("scroll", scrolls, { passive: true });
    return () => { window.removeEventListener("click", clicks); window.removeEventListener("scroll", scrolls); };
  }, [page, beacon]);

  const c = page?.content || {};
  const t = page?.tracking || {};
  const meta = c.meta || {};
  const hero = c.hero || {};
  const nav = c.nav || {};
  const form = c.form || {};
  const faq = c.faq || [];
  const logo = platformLogoUrl(nav.logo_url || LOGO_URL);
  const payload = useMemo(() => ({ ...page, categories: page?.categories || [], timelines: page?.timelines || [] }), [page]);
  const goForm = () => {
    const el = document.getElementById("gads-lead");
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 64;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };
  const featIcons = [Zap, ShieldCheck, Smartphone, MessageCircle, IndianRupee, Sparkles];

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.filter((x) => x?.q && x?.a).map((x) => ({
      "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a },
    })),
  };

  return (
    <div className="gads-lp" data-testid="gads-landing">
      <Helmet>
        <title>{meta.title || "India's Best Shopify Alternative"}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="description" content={meta.description || ""} />
        <meta name="keywords" content={meta.keywords || ""} />
        <link rel="canonical" href={`${typeof window !== "undefined" ? window.location.origin : ""}/india-shopify-alternative`} />
        {faq.length ? <script type="application/ld+json">{JSON.stringify(faqLd)}</script> : null}
      </Helmet>
      {page ? <Tracking t={t} /> : null}

      <header className="gads-nav">
        <div className="gads-wrap gads-nav-inner">
          <a className="gads-brand" href="/" data-testid="gads-logo">
            <img src={logo} alt={nav.logo_text || "Mera Startup"} />
          </a>
          <div className="gads-nav-actions">
            <button type="button" className="gads-btn gads-btn-primary gads-nav-demo" onClick={goForm} data-testid="gads-book-demo">Book Free Demo</button>
            <a className="gads-btn gads-btn-buy gads-nav-buy" href={hero.buy_url || "/signup"} data-testid="gads-buy">{nav.cta_buy || "Start for ₹1999"}</a>
          </div>
        </div>
      </header>

      <main className="gads-wrap">
        <section className="gads-hero">
          <div className="gads-hero-copy">
            {hero.badge ? <div className="gads-badge"><Sparkles size={14} /> {hero.badge}</div> : null}
            <h1>{hero.h1 || "India's Best Shopify Alternative"}</h1>
            <p className="sub">{hero.sub}</p>
            <div className="gads-price-line">{hero.price_line}</div>
            <div className="gads-funnel" data-testid="gads-funnel">
              {FUNNEL.map((s, i) => (
                <div className={`gads-step ${i === 2 ? "is-hot" : ""}`} key={s.n}>
                  <b>Step {s.n}</b>
                  <span>{s.title}</span>
                </div>
              ))}
            </div>
            <div className="gads-hero-ctas">
              <button type="button" className="gads-btn gads-btn-primary" onClick={goForm}>{hero.primary_cta || "Book free demo"} <ArrowRight size={16} /></button>
              <a className="gads-btn gads-btn-buy" href={hero.buy_url || "/signup"}>{hero.buy_cta || "Buy now"}</a>
            </div>
            <p className="gads-proof"><Star size={14} /> {hero.proof}</p>
          </div>
          <div className="gads-hero-form">
            <div className="gads-form-card" id="gads-lead">
              <h3>{form.title || "Get a free store walkthrough"}</h3>
              <p>{form.subtitle}</p>
              {page ? <LeadForm c={payload} placement="inline" /> : <p>Loading form…</p>}
            </div>
          </div>
        </section>

        <div className="gads-trust">
          {(c.trust_bar || []).map((x) => (
            <span className="gads-chip" key={x}><Check size={14} color="#0f766e" /> {x}</span>
          ))}
        </div>

        <div className="gads-stats">
          {(c.stats || []).map((s) => (
            <div className="gads-stat" key={s.label}><b>{s.value}</b><span>{s.label}</span></div>
          ))}
        </div>

        <section className="gads-section">
          <h2>{c.pain?.title}</h2>
          <p className="lead">{c.pain?.subtitle}</p>
          <div className="gads-pain">
            {(c.pain?.items || []).map((it) => (
              <div className="gads-card" key={it.title}>
                <div className="gads-ico warn">!</div>
                <h3>{it.title}</h3>
                <p>{it.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="gads-section">
          <h2>{c.compare?.title}</h2>
          <p className="lead">{c.compare?.subtitle}</p>
          <div className="gads-table-scroll">
          <table className="gads-compare">
            <thead>
              <tr>
                <th>Feature</th>
                <th>{c.compare?.us_label || "Mera Startup"}</th>
                <th>{c.compare?.them_label || "Shopify"}</th>
              </tr>
            </thead>
            <tbody>
              {(c.compare?.rows || []).map((row) => (
                <tr key={row.feature}>
                  <td>{row.feature}</td>
                  <td>{row.us}</td>
                  <td>{row.shopify}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </section>

        <section className="gads-section">
          <h2>Everything an Indian store needs</h2>
          <div className="gads-feats">
            {(c.features || []).map((it, i) => {
              const Icon = featIcons[i % featIcons.length];
              return (
                <div className="gads-card" key={it.title}>
                  <div className="gads-ico"><Icon size={18} /></div>
                  <h3>{it.title}</h3>
                  <p>{it.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="gads-section">
          <h2>{c.pricing?.title}</h2>
          <p className="lead">{c.pricing?.subtitle}</p>
          <div className="gads-plans">
            {(c.pricing?.plans || []).map((p) => (
              <div className="gads-plan" key={p.name}>
                <div className="gads-badge">{p.badge}</div>
                <h3>{p.name}</h3>
                <div className="price">{p.price} <small>{p.period}</small></div>
                <ul>{(p.bullets || []).map((b) => <li key={b}>{b}</li>)}</ul>
                <button type="button" className="gads-btn gads-btn-primary" onClick={goForm}>{p.cta}</button>
              </div>
            ))}
          </div>
          <p className="lead">{c.pricing?.footnote}</p>
        </section>

        <section className="gads-section">
          <h2>Merchants who switched</h2>
          <div className="gads-quotes">
            {(c.testimonials || []).map((q) => (
              <div className="gads-card gads-quote" key={q.name}>
                <div className="gads-stars">★★★★★</div>
                <p>“{q.quote}”</p>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
                  <div className="gads-avatar">{(q.name || "?").slice(0, 1)}</div>
                  <div>
                    <b>{q.name}</b>
                    <div className="lead" style={{ margin: 0 }}>{q.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="lead">{c.logos_note}</p>
        </section>

        <section className="gads-section gads-faq">
          <h2>Questions advertisers ask</h2>
          {faq.map((x) => (
            <details key={x.q}><summary>{x.q}<span>+</span></summary><p>{x.a}</p></details>
          ))}
        </section>

        <section className="gads-cta-band">
          <div>
            <h2 style={{ margin: "0 0 6px" }}>{c.guarantee?.title}</h2>
            <p style={{ margin: 0, color: "#cbd5e1" }}>{c.guarantee?.body}</p>
          </div>
          <div className="gads-hero-ctas">
            <button type="button" className="gads-btn gads-btn-primary" onClick={goForm}>Book demo</button>
            <a className="gads-btn gads-btn-buy" href={hero.buy_url || "/signup"}>Buy now</a>
            <Link className="gads-btn gads-btn-ghost" to={SIGNUP_HREF} data-testid="gads-create-store">Create store</Link>
          </div>
        </section>
      </main>

      <div className="gads-foot">{c.footer_note || "Indian ecommerce, without the Shopify tax."}</div>
      <div className="gads-sticky" data-testid="gads-sticky">
        <div className="gads-sticky-note">Takes 20 seconds · we WhatsApp you</div>
        <div className="gads-sticky-row">
          <button type="button" className="gads-btn gads-btn-primary" onClick={goForm} data-testid="gads-sticky-demo">Book Free Demo</button>
          <a className="gads-btn gads-btn-buy" href={hero.buy_url || SIGNUP_HREF} data-testid="gads-sticky-buy">Start ₹1999</a>
        </div>
      </div>

      {popup && page ? (
        <div className="gads-modal" data-testid="gads-popup">
          <div className="gads-form-card gads-form-card-popup">
            <button type="button" className="gads-close" aria-label="Close" onClick={closePopup}>×</button>
            <h3>{form.popup_title || "Claim your free demo"}</h3>
            <p>{form.popup_subtitle}</p>
            <LeadForm c={payload} placement="popup" onDone={closePopup} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
